import { ApolloError } from "apollo-client"
import * as Sentry from "@sentry/browser"
import { FORM_ERROR } from "final-form"

interface ErrorMessageMap {
  [key: string]: string
}

type ErrorValidationField = {
  [key: string]: ErrorMessageMap
}

const DEFAULT_MESSAGE = "Uh oh, something went wrong. Please refresh and try again, or contact us at care@withcove.com."
const CUSTOM_ERROR_MESSAGES: ErrorMessageMap = {
  "Bad Credentials": "Invalid Email or password.",
  "Invalid hash": "Invalid returning user hash.",
  underage: "You need to be 18 years or older to use Cove.",
  out_of_state: "Unfortunately, we do not offer Cove in your state."
}
const VALIDATION_ERRORS: ErrorValidationField = {
  email: {
    email_exists: "An account with this email address already exists in our system. Please sign in.",
    email_undeliverable: "Please provide a real email."
  }
}
export const EMAIL_EXISTS_MESSAGE = "Email already exists"

export const getDisplayedErrorMessage = (ex?: ApolloError): string | undefined => {
  if (!ex) {
    return undefined
  }

  const displayedErrorMessages = ex.graphQLErrors.length
    ? ex.graphQLErrors.map(({ message }) => CUSTOM_ERROR_MESSAGES[message] || message).join(" ")
    : DEFAULT_MESSAGE

  Sentry.withScope(scope => {
    scope.setExtra("displayedErrorMessages", displayedErrorMessages)
  })

  return displayedErrorMessages
}

export const getFormErrors = (error: ApolloError | Error | string) => {
  if (error instanceof ApolloError) {
    let fieldErrors = {}
    const commonErrors: string[] = []
    const { graphQLErrors, networkError } = error

    graphQLErrors.map(({ message, extensions }) => {
      // Handle extension field validation errors
      if (extensions && extensions.fields) {
        const errorFields: ErrorMessageMap = extensions.fields

        fieldErrors = Object.entries(errorFields).reduce((result, [fieldKey, value]) => {
          const errorMapper = VALIDATION_ERRORS[fieldKey]

          return { ...result, [fieldKey]: errorMapper[value] }
        }, {})
      }

      // Handle common errors
      commonErrors.push(CUSTOM_ERROR_MESSAGES[message] || message)
    })

    if (networkError) {
      // Push the default error message, native network error message will be useless for users
      commonErrors.push(DEFAULT_MESSAGE)
    }

    return {
      [FORM_ERROR]: commonErrors.join(" "),
      ...fieldErrors
    }
  } else {
    let errorMessage = DEFAULT_MESSAGE

    if (error instanceof Error) {
      errorMessage = error.message

      Sentry.captureException(error)
    } else if (typeof error === "string") {
      errorMessage = error

      Sentry.captureMessage(error)
    }

    return { [FORM_ERROR]: errorMessage }
  }
}
